// SpectraLearn Accessibility provider  -  implements WCAG 2.2 user preferences
// per the Accessibility specification: OpenDyslexic toggle, font-size slider,
// reduce-motion, high-contrast, reading-bg tint. Persists to localStorage.
import { createContext, useContext, useEffect, useState, useCallback } from "react";

const DEFAULT = {
  dyslexicFont: false,
  fontScale: 100,       // 100 | 112 | 124 | 140
  reduceMotion: false,
  highContrast: false,
  bgTint: "default",    // default | cream | blue
};

const AccessibilityContext = createContext({ prefs: DEFAULT, update: () => {}, reset: () => {} });

const STORE_KEY = "spectralearn-a11y-prefs-v1";

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return DEFAULT;
    return { ...DEFAULT, ...JSON.parse(raw) };
  } catch { return DEFAULT; }
}

function applyToDom(prefs) {
  const body = document.body;
  const html = document.documentElement;
  body.classList.toggle("a11y-dyslexic", !!prefs.dyslexicFont);
  body.classList.toggle("reduce-motion", !!prefs.reduceMotion);
  body.classList.toggle("a11y-hc", !!prefs.highContrast);
  body.classList.toggle("a11y-bg-cream", prefs.bgTint === "cream");
  body.classList.toggle("a11y-bg-blue", prefs.bgTint === "blue");
  html.setAttribute("data-a11y-fontscale", String(prefs.fontScale || 100));
}

export function AccessibilityProvider({ children }) {
  const [prefs, setPrefs] = useState(loadPrefs);

  useEffect(() => {
    applyToDom(prefs);
    try { localStorage.setItem(STORE_KEY, JSON.stringify(prefs)); } catch { /* noop */ }
  }, [prefs]);

  // Respect OS-level prefers-reduced-motion at first load if user hasn't opted in
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (m.matches && !prefs.reduceMotion) {
      setPrefs((p) => ({ ...p, reduceMotion: true }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback((patch) => setPrefs((p) => ({ ...p, ...patch })), []);
  const reset  = useCallback(() => setPrefs(DEFAULT), []);

  return (
    <AccessibilityContext.Provider value={{ prefs, update, reset }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useA11y() { return useContext(AccessibilityContext); }
